/**
 * The hub: creates every agent (board, sim, render, input, audio, haptics, coach, levels, UI)
 * and connects them. Each agent stays simple; this file is the wiring diagram.
 */
import { Brain } from './coach/brain';
import { Sparky } from './coach/sparky';
import { Voice } from './coach/voice';
import { Board, flipped, rotated, type BoardJSON, type PlaceProblem } from './core/board';
import { CommandError, History, apply, type Command } from './core/commands';
import type { PartProps, PartType, Placement } from './core/types';
import { Audio, Haptics } from './fx/audio';
import { Controller, type ControllerHost, type Feedback } from './input/controller';
import { LevelRun, loadLevel, nextMissing, remaining, sameItem } from './levels/engine';
import type { Level, TrayItem } from './levels/types';
import { WORLD1 } from './levels/world1';
import { WORLD2 } from './levels/world2';
import type { MotorState, BuzzerState, BatteryState } from './parts/defs';
import { LED_COLORS } from './parts/defs';
import { partDef } from './parts/registry';
import { WIRE_COLOR_NAMES } from './render/kit';
import { Stage, type Quality } from './render/stage';
import { renderThumbs } from './render/thumbs';
import { canPrompt, isInstalled, isIosSafari, onInstallChange, promptInstall } from './pwa';
import { findSpot } from './core/spot';
import { readyToFlip } from './sim/insight';
import { Sim, TICK, type SimEvent } from './sim/sim';
import { confetti } from './ui/confetti';
import { h } from './ui/dom';
import { Tray, type TrayEntry } from './ui/tray';

export const ALL_LEVELS: Level[] = [...WORLD1, ...WORLD2];

const WORLDS = [
  { num: 1, title: 'First Spark', emoji: '⚡' },
  { num: 2, title: 'Many Things', emoji: '🌈' },
  { num: 3, title: 'One-Way Streets', emoji: '🚦' },
  { num: 4, title: 'Sense It', emoji: '🧲' },
  { num: 5, title: 'Inventor', emoji: '💡' },
];

const FREE_TRAY: Omit<TrayEntry, 'key'>[] = [
  { type: 'battery', props: {}, label: 'Battery' },
  { type: 'lamp', props: {}, label: 'Bulb' },
  { type: 'led', props: { color: 'red' }, label: 'LED' },
  { type: 'motor', props: {}, label: 'Motor' },
  { type: 'switch', props: {}, label: 'Switch' },
  { type: 'button', props: {}, label: 'Button' },
  { type: 'buzzer', props: {}, label: 'Buzzer' },
  { type: 'resistor', props: { ohms: 100 }, label: 'Resistor' },
  { type: 'wire', props: { length: 3, color: 'blue' }, label: 'Wire' },
];

/** A fresh Free Build board: elastic, so it grows as you build. */
function freeBoard(): Board {
  const b = new Board(10, 7);
  b.elastic = true;
  return b;
}

const thumbKey = (type: PartType, props: PartProps = {}) =>
  `${type}:${JSON.stringify(Object.entries({ ...partDef(type).defaultProps(), ...props }).sort())}`;

const store = {
  get<T>(k: string): T | null {
    try {
      const v = localStorage.getItem(`zapboard:${k}`);
      return v ? (JSON.parse(v) as T) : null;
    } catch {
      return null;
    }
  },
  set(k: string, v: unknown) {
    try {
      localStorage.setItem(`zapboard:${k}`, JSON.stringify(v));
    } catch {
      /* private mode etc. */
    }
  },
};

type Mode = { kind: 'free' } | { kind: 'level'; level: Level; run: LevelRun; locked: Set<string> };

export class App implements ControllerHost {
  board: Board;
  sim: Sim;
  history: History;
  readonly stage: Stage;
  readonly controller: Controller;
  readonly audio = new Audio();
  readonly voice = new Voice();
  readonly sparky: Sparky;
  readonly brain: Brain;
  private tray: Tray;
  private thumbs: Map<string, string>;
  private mode: Mode = { kind: 'free' };
  private ui: HTMLElement;
  private menu: HTMLElement;
  private levelbar: HTMLElement;
  /** Coach card parts (phones): Sparky's face and the slot his words appear in. */
  private avatarEl!: HTMLElement;
  private speechEl!: HTMLElement;
  private speechTimer = 0;
  private phone = false;
  private titleEl: HTMLElement;
  private undoBtn!: HTMLButtonElement;
  private redoBtn!: HTMLButtonElement;
  private layout: 'left' | 'bottom' = 'left';
  private acc = 0;
  private last = 0;
  private menuFor: string | null = null;
  private lastProgressAt = 0;
  private nudged = false;
  private saveTimer = 0;
  private winTimer = 0;
  paused = false;

  constructor(root: HTMLElement, quality: Quality) {
    const canvas = h('canvas#stage', { 'aria-label': 'Zapboard building board' });
    this.ui = h('div.ui');
    root.append(canvas, this.ui);

    this.board = new Board(10, 8);
    this.sim = new Sim(this.board);
    this.history = new History(this.board);
    this.stage = new Stage(canvas, this.board, quality);
    this.controller = new Controller(this);

    // Thumbnails for every tray item we might show.
    const items = [
      ...FREE_TRAY.map((t) => ({ key: thumbKey(t.type, t.props), type: t.type, props: t.props })),
      ...ALL_LEVELS.flatMap((l) =>
        l.tray.map((t) => ({ key: thumbKey(t.type, t.props), type: t.type, props: t.props ?? {} })),
      ),
    ];
    this.thumbs = renderThumbs([...new Map(items.map((i) => [i.key, i])).values()]);

    this.tray = new Tray(
      this.ui,
      this.thumbs,
      (entry, e) => this.controller.beginNew(entry.type, entry.props, e),
      (entry) => this.trayTap(entry),
    );
    this.sparky = new Sparky(this.ui, this.voice);
    this.brain = new Brain(this.sparky);
    this.sparky.onTap = () => this.hint();
    this.avatarEl = h('button.lb-avatar', {
      'aria-label': 'Sparky, your helper. Tap for a hint.',
      html: sparkySvg(this.sparky, 'card'),
      onclick: () => this.hint(),
    });
    this.speechEl = h('div.lb-speech', { role: 'status', 'aria-live': 'polite' });

    this.titleEl = h('div.title');
    this.ui.append(this.topbar());
    this.menu = h('div.part-menu', { role: 'menu' });
    this.levelbar = h('div.levelbar', { style: { display: 'none' } });
    this.ui.append(this.menu, this.levelbar);

    window.addEventListener('resize', () => this.relayout());
    window.addEventListener('keydown', (e) => this.onKey(e));
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.voice.stop();
    });
    this.relayout();
    requestAnimationFrame((t) => this.frame(t));
  }

  // ───────────────────────────── Modes ─────────────────────────────
  private setBoard(board: Board) {
    this.board = board;
    this.sim = new Sim(board);
    this.history = new History(board);
    this.stage.setBoard(board);
    this.stage.sync(true);
    this.select(null);
    this.relayout();
  }

  startFree() {
    this.closeOverlays();
    this.stage.showHint(null);
    const saved = store.get<BoardJSON>('free');
    let board: Board;
    try {
      board = saved?.v === 1 ? Board.fromJSON(saved) : freeBoard();
      board.elastic = true;
      board.grow();
    } catch {
      board = freeBoard();
    }
    this.mode = { kind: 'free' };
    this.setBoard(board);
    this.titleEl.textContent = '🛠️ Free Build';
    this.setCard(
      'free',
      [h('div.free-tip', {}, '🛠️ Build anything! Drag parts on, then draw wires from the silver snaps.')],
      [],
    );
    this.tray.set(FREE_TRAY.map((t) => ({ ...t, key: thumbKey(t.type, t.props) })));
    this.updateHistoryButtons();
    this.relayout();
    store.set('last', 'free');
    if (!board.parts.size)
      this.brain.say(
        'Free build! Drag parts onto the board, then drag from a silver snap to draw a wire. The board grows as you build! 🛠️',
        'happy',
      );
  }

  startLevel(level: Level) {
    this.closeOverlays();
    window.clearTimeout(this.winTimer);
    const { board, locked } = loadLevel(level);
    this.mode = { kind: 'level', level, run: new LevelRun(level, performance.now()), locked };
    this.setBoard(board);
    this.titleEl.textContent = `${level.emoji} ${level.num}. ${level.title}`;
    this.tray.set(
      level.tray.map((t) => ({
        key: thumbKey(t.type, t.props),
        type: t.type,
        props: { ...partDef(t.type).defaultProps(), ...t.props },
        label: trayLabel(t),
        left: remaining(t, board, locked),
      })),
    );
    this.renderLevelbar();
    this.lastProgressAt = performance.now();
    this.nudged = false;
    this.updateHint(!!level.guide);
    this.updateHistoryButtons();
    this.relayout();
    store.set('last', level.id);
    this.guess = null;
    if (level.predict) window.setTimeout(() => this.predictCard(level), 350);
    else window.setTimeout(() => this.brain.say(level.intro, 'happy'), 450);
  }

  private guess: string | null = null;

  /** Predict-then-try: one big question before building. */
  private predictCard(level: Level) {
    const pr = level.predict!;
    const choose = (id: string) => {
      this.guess = id;
      store.set(`guess:${level.id}`, id);
      this.audio.pop();
      Haptics.tick();
      this.closeOverlays();
      this.brain.say(`Good guess! Let's find out. ${level.intro}`, 'wow');
    };
    const card = h(
      'div.card.predict',
      {},
      h('div.predict-tag', {}, '🔮 Make a guess'),
      h('h2', {}, pr.question),
      h(
        'div.choices',
        {},
        pr.choices.map((c) =>
          h(
            'button.choice',
            { onclick: () => choose(c.id) },
            h('span.choice-emoji', {}, c.emoji),
            h('span', {}, c.text),
          ),
        ),
      ),
      h(
        'button.skip',
        {
          onclick: () => {
            this.closeOverlays();
            this.brain.say(level.intro, 'happy');
          },
        },
        'Skip, just build',
      ),
    );
    this.overlay(card, false);
    this.voice.say(`${pr.question} ${pr.choices.map((c) => c.text).join(', or ')}?`);
  }

  private get levelMode() {
    return this.mode.kind === 'level' ? this.mode : null;
  }

  // ───────────────────────────── Host API (for the input agent) ─────────────────────────────
  run(cmd: Command, opts: { snapped?: string } = {}): boolean {
    try {
      this.history.run(cmd);
    } catch (e) {
      if (e instanceof CommandError) {
        this.feedback('invalid', (e.message as PlaceProblem) || undefined);
        return false;
      }
      throw e;
    }
    this.afterChange();
    if (opts.snapped) {
      this.stage.bump(opts.snapped);
      this.audio.snap();
      Haptics.snap();
    }
    return true;
  }

  toggle(id: string) {
    const p = this.board.parts.get(id);
    if (!p) return;
    apply(this.board, { op: 'set', id, key: 'on', value: !p.props.on });
    this.lastProgressAt = performance.now();
    this.audio.click();
    Haptics.tick();
    this.scheduleSave();
  }

  press(id: string, down: boolean) {
    this.sim.setInput(id, 'pressed', down);
    if (down) {
      this.audio.click();
      Haptics.tick();
    }
  }

  select(id: string | null) {
    if (id && !this.board.parts.has(id)) id = null;
    this.stage.select(id);
    this.menuFor = id;
    if (id) this.buildMenu(id);
    this.menu.classList.toggle('show', !!id);
    if (id) this.audio.pop();
  }

  info(id: string) {
    const p = this.board.parts.get(id);
    if (p) this.factCard(p.type, p.props);
  }

  feedback(kind: Feedback, problem?: PlaceProblem | 'stuck') {
    switch (kind) {
      case 'tick':
        this.audio.tick();
        Haptics.buzz(4);
        break;
      case 'draw':
        this.audio.zip();
        Haptics.tick();
        break;
      case 'pickup':
        this.audio.pickup();
        Haptics.tick();
        break;
      case 'drop':
        this.audio.drop();
        break;
      case 'poof':
        this.audio.poof();
        Haptics.tick();
        break;
      case 'invalid':
      case 'stuck':
        this.audio.invalid();
        Haptics.invalid();
        if (problem) this.brain.onProblem(problem);
        break;
    }
  }

  canEdit(id: string, quiet = false): boolean {
    const lm = this.levelMode;
    if (lm?.locked.has(id)) {
      if (!quiet) this.brain.onProblem('locked');
      return false;
    }
    return true;
  }

  overTray(x: number, y: number): boolean {
    const r = this.tray.el.getBoundingClientRect();
    return x >= r.left - 8 && x <= r.right + 8 && y >= r.top - 8 && y <= r.bottom + 8;
  }

  trashMode(on: boolean, hot = false) {
    this.tray.el.classList.toggle('trash', on);
    this.tray.el.classList.toggle('hot', on && hot);
  }

  private wireColor = 0;
  nextWireColor(): string {
    const lm = this.levelMode;
    const levelColor = lm?.level.tray.find((t) => t.type === 'wire')?.props?.color;
    if (levelColor) return String(levelColor);
    const names = ['blue', 'red', 'green', 'orange', 'purple', 'yellow'];
    return names[this.wireColor++ % names.length]!;
  }

  private drewWire = store.get<boolean>('drewWire') ?? false;
  onWireDrawn() {
    if (this.drewWire) return;
    this.drewWire = true;
    store.set('drewWire', true);
    this.brain.say('You drew a wire! Grab its silver end to stretch it anywhere. ✏️', 'wow');
  }

  // ───────────────────────────── Changes ─────────────────────────────
  private dims = '';
  private afterChange() {
    this.stage.sync();
    // The board grew (or shrank): re-plan the layout around its new shape.
    const dims = `${this.board.cols}x${this.board.rows}`;
    if (dims !== this.dims) {
      this.dims = dims;
      this.relayout();
    }
    this.lastProgressAt = performance.now();
    this.coachCheck();
    if (this.menuFor && !this.board.parts.has(this.menuFor)) this.select(null);
    else if (this.menuFor) this.buildMenu(this.menuFor);
    this.updateHistoryButtons();
    const lm = this.levelMode;
    if (lm) {
      const left = new Map<string, number | undefined>();
      for (const t of lm.level.tray) left.set(thumbKey(t.type, t.props), remaining(t, this.board, lm.locked));
      this.tray.updateCounts(left);
      this.updateHint(lm.run.hintsUsed > 0 || !!lm.level.guide);
    }
    this.scheduleSave();
  }

  /** One tap away from working? Tell the kid exactly what to do next. */
  private coachTimer = 0;
  private coachSaid = '';
  private coachCheck() {
    window.clearTimeout(this.coachTimer);
    // Wait a moment so the circuit settles (and the kid can finish what they're doing).
    this.coachTimer = window.setTimeout(() => {
      const what = readyToFlip(this.board, this.sim);
      const key = `${what}:${this.board.version}`;
      if (!what || this.coachSaid === key) return;
      this.coachSaid = key;
      this.brain.say(
        what === 'switch'
          ? 'Your loop is ready! Tap the switch to turn it ON. 👆'
          : what === 'switches'
            ? 'Your loop is ready! Turn ON all the switches. 👆'
            : 'Your loop is ready! Press and hold the button. 👆',
        'wow',
      );
    }, 700);
  }

  private scheduleSave() {
    if (this.mode.kind !== 'free') return;
    window.clearTimeout(this.saveTimer);
    this.saveTimer = window.setTimeout(() => store.set('free', this.board.toJSON()), 400);
  }

  undo() {
    if (this.history.undo()) {
      this.audio.pop();
      this.afterChange();
    }
  }

  redo() {
    if (this.history.redo()) {
      this.audio.pop();
      this.afterChange();
    }
  }

  clearBoard() {
    if (this.mode.kind === 'level') {
      this.startLevel(this.mode.level);
      return;
    }
    if (!this.board.parts.size) return;
    this.run({
      op: 'batch',
      cmds: [...this.board.parts.keys()].map((id) => ({ op: 'remove' as const, id })),
    });
    this.audio.poof();
  }

  private updateHistoryButtons() {
    if (!this.undoBtn) return;
    this.undoBtn.disabled = !this.history.canUndo;
    this.redoBtn.disabled = !this.history.canRedo;
  }

  // ───────────────────────────── Main loop ─────────────────────────────
  private frame(t: number) {
    requestAnimationFrame((tt) => this.frame(tt));
    const dt = Math.min(0.1, this.last ? (t - this.last) / 1000 : 0);
    this.last = t;
    if (this.paused) return;
    this.acc += dt;
    let steps = 0;
    while (this.acc >= TICK && steps < 6) {
      const events = this.sim.step();
      for (const e of events) this.onSimEvent(e);
      this.levelTick(events);
      this.acc -= TICK;
      steps++;
    }
    if (steps === 6) this.acc = 0;
    this.continuousSound();
    this.stage.update(this.sim, dt);
    this.positionMenu();
    this.stage.render();
  }

  private onSimEvent(e: SimEvent) {
    switch (e.kind) {
      case 'lit':
        this.audio.lit();
        break;
      case 'short':
        this.audio.zap();
        Haptics.short();
        this.stage.shake = 0.6;
        break;
      case 'launch':
        this.audio.launch();
        Haptics.launch();
        this.stage.shake = 0.5;
        {
          const s = this.stage.screenOf(e.id, 1);
          window.setTimeout(() => confetti(s ?? undefined, 90), 350);
        }
        break;
      case 'landed':
        this.audio.boing();
        Haptics.tick();
        break;
    }
    this.brain.onSim(e);
  }

  private continuousSound() {
    let omega = 0;
    let buzz = 0;
    let heat = 0;
    for (const p of this.board.parts.values()) {
      if (p.type === 'motor') {
        const s = this.sim.state<MotorState>(p.id);
        if (s) omega = Math.max(omega, Math.abs(s.fanAttached ? s.omega : s.omega * 1.4));
      } else if (p.type === 'buzzer') buzz = Math.max(buzz, this.sim.state<BuzzerState>(p.id)?.level ?? 0);
      else if (p.type === 'battery') {
        const s = this.sim.state<BatteryState>(p.id);
        if (s?.shorted) heat = Math.max(heat, 0.4 + s.heat * 0.6);
      }
    }
    this.audio.motor(omega);
    this.audio.buzzer(buzz);
    this.audio.short(heat);
  }

  // ───────────────────────────── Levels ─────────────────────────────
  private levelTick(events: SimEvent[]) {
    const lm = this.levelMode;
    if (!lm) return;
    const wasDone = lm.run.done;
    const prog = lm.run.tick(this.board, this.sim, events);
    if (prog.justCompleted >= 0) {
      this.lastProgressAt = performance.now();
      this.renderLevelbar();
      this.audio.star(prog.justCompleted);
      Haptics.tick();
      if (!prog.done) {
        const next = lm.level.steps[prog.step];
        window.setTimeout(
          () => this.brain.say(`Great! ${(next?.text ?? '').replace(/[!.]+$/, '')}!`, 'wow'),
          600,
        );
      }
    }
    if (prog.done && !wasDone) this.win(lm);
    // Gentle nudge if stuck for a while.
    if (
      !lm.run.done &&
      !this.nudged &&
      performance.now() - this.lastProgressAt > 30000 &&
      !this.controller.dragging
    ) {
      this.nudged = true;
      this.brain.say('Need a little help? Tap me or the 💡 button for a hint!', 'think');
    }
  }

  /**
   * The coach card has three fixed slots: avatar, message (goal or speech), actions.
   * On phones Sparky talks INSIDE the message slot, so words can never cover a button.
   */
  private setCard(kind: 'goal' | 'won' | 'free', message: (Node | string)[], actions: Node[]) {
    this.levelbar.classList.toggle('won', kind === 'won');
    this.levelbar.classList.toggle('free', kind === 'free');
    this.levelbar.classList.toggle('has-actions', actions.length > 0);
    this.levelbar.replaceChildren(
      this.avatarEl,
      h('div.lb-msg', {}, h('div.lb-goal', {}, message), this.speechEl),
      h('div.lb-actions', {}, actions),
    );
    this.levelbar.style.display = '';
  }

  /** Phones: show Sparky's words in the card's message slot for a while, then the goal returns. */
  private cardSay(text: string, holdMs: number) {
    this.speechEl.textContent = text;
    this.levelbar.classList.add('speaking');
    this.avatarEl.classList.add('talking');
    window.clearTimeout(this.speechTimer);
    this.speechTimer = window.setTimeout(() => this.levelbar.classList.remove('speaking'), holdMs);
  }

  private renderLevelbar() {
    const lm = this.levelMode;
    if (!lm) return;
    const steps = lm.level.steps.map((s, i) =>
      h(
        `div.step${i < lm.run.step ? '.done' : i === lm.run.step ? '.active' : ''}` as 'div',
        {},
        h('span.check', {}, i < lm.run.step ? '✓' : ''),
        s.text,
      ),
    );
    const n = lm.level.steps.length;
    this.setCard(
      'goal',
      [
        // Phones show just the current goal; this tells kids how far along they are.
        n > 1
          ? h(
              'span.step-count',
              { 'aria-label': `Step ${lm.run.step + 1} of ${n}` },
              `${Math.min(lm.run.step + 1, n)}/${n}`,
            )
          : '',
        h('div.steps', {}, steps),
      ],
      [
        h(
          'button.icon-btn.hint-btn',
          { 'aria-label': 'Hint', title: 'Hint', onclick: () => this.hint() },
          '💡',
        ),
      ],
    );
  }

  hint() {
    const lm = this.levelMode;
    if (!lm) {
      this.brain.say('Tap a part in the tray to learn about it, or long-press a part on the board!', 'happy');
      return;
    }
    if (lm.run.done) return;
    const text = lm.run.nextHint();
    this.brain.say(text, 'think');
    this.updateHint(true);
  }

  private updateHint(show: boolean) {
    const lm = this.levelMode;
    if (!lm || !show || lm.run.done) {
      this.stage.showHint(null);
      return;
    }
    const miss = nextMissing(lm.level, this.board);
    if (!miss) {
      this.stage.showHint(null);
      return;
    }
    const props = { ...partDef(miss.type).defaultProps(), ...miss.props };
    const p: Placement = {
      id: '__hint',
      type: miss.type,
      x: miss.x,
      y: miss.y,
      rot: miss.rot,
      layer: 0,
      props,
    };
    this.stage.showHint(this.board.check(p).ok ? p : null);
  }

  private win(lm: Extract<Mode, { kind: 'level' }>) {
    this.stage.showHint(null);
    const stars = lm.run.stars;
    const progress = store.get<Record<string, number>>('progress') ?? {};
    progress[lm.level.id] = Math.max(progress[lm.level.id] ?? 0, stars);
    store.set('progress', progress);
    this.audio.success();
    Haptics.success();
    this.sparky.cheer();
    confetti();
    // No blocking pop-up: the checklist turns into a "done!" banner and the circuit stays playable.
    this.renderWonBar(lm.level, stars);
    const launch = lm.level.steps.some((s) => 'event' in s.when && s.when.event === 'launch');
    const pr = lm.level.predict;
    const guess = this.guess;
    const revealLine = pr
      ? guess === pr.answer
        ? `You guessed it! 🔮 ${pr.reveal}`
        : guess
          ? `Surprise! ${pr.reveal}`
          : pr.reveal
      : null;
    this.winTimer = window.setTimeout(
      () =>
        revealLine
          ? this.brain.say(`You did it! ${revealLine}`, 'wow')
          : this.brain.say(
              'You did it! 🎉 Keep playing with your circuit, or tap Next. Tap 💡 to learn something cool!',
              'wow',
            ),
      launch ? 2600 : 900,
    );
  }

  /** The level bar after winning: stars, "Did you know?", and Next, without covering the board. */
  private renderWonBar(level: Level, stars: number) {
    const idx = ALL_LEVELS.indexOf(level);
    const next = ALL_LEVELS[idx + 1];
    const starEls = [0, 1, 2].map((i) =>
      h(
        `span.star${i < stars ? '.on' : ''}` as 'span',
        { style: { animationDelay: `${0.15 + i * 0.2}s` } },
        '⭐',
      ),
    );
    this.setCard(
      'won',
      [h('div.won-text', {}, h('b', {}, 'Level complete! 🎉'), h('span.won-stars', {}, starEls))],
      [
        h(
          'button.icon-btn',
          { 'aria-label': 'Play again', title: 'Play again', onclick: () => this.startLevel(level) },
          '↺',
        ),
        h(
          'button.icon-btn',
          {
            'aria-label': 'Did you know?',
            title: 'Did you know?',
            onclick: () => this.brain.say(`Did you know? ${level.outro}`, 'happy'),
          },
          '💡',
        ),
        h(
          'button.icon-btn.opt',
          { 'aria-label': 'All levels', title: 'All levels', onclick: () => this.showMap() },
          '🗺️',
        ),
        next
          ? h(
              'button.btn.yellow.next-btn',
              { onclick: () => this.startLevel(next) },
              next.world !== level.world ? `World ${next.world} ▶` : 'Next ▶',
            )
          : h('button.btn.yellow.next-btn', { onclick: () => this.startFree() }, 'Free Build 🛠️'),
      ],
    );
    starEls.forEach((_, i) => i < stars && window.setTimeout(() => this.audio.star(i), 150 + i * 200));
    this.relayout();
  }

  // ───────────────────────────── Screens ─────────────────────────────
  showGate() {
    const sparky = h('div.sparky-hero', { html: sparkySvg(this.sparky, 'hero') });
    const progress = store.get<Record<string, number>>('progress') ?? {};
    const played = Object.keys(progress).length > 0;
    const go = (fn: () => void) => () => {
      this.audio.unlock();
      this.voice.unlock();
      Haptics.tick();
      gate.remove();
      this.paused = false;
      fn();
    };
    const gate = h(
      'div.gate',
      {},
      sparky,
      h('div.hero-logo', {}, 'Zapboard'),
      h(
        'div.tag',
        {},
        'Snap together batteries, bulbs, motors and more, and watch real electricity bring them to life! ⚡',
      ),
      h(
        'div.row',
        {},
        h(
          'button.btn.big.yellow',
          {
            onclick: go(() => {
              if (this.mode.kind === 'level') this.startLevel(this.mode.level);
              else if (played) this.showMap();
              else this.startLevel(ALL_LEVELS[0]!);
            }),
          },
          played ? "Let's Play! ⚡" : "Let's Build! ⚡",
        ),
        h('button.btn.big.white', { onclick: go(() => this.startFree()) }, 'Free Build 🛠️'),
      ),
      h('div.fine', {}, 'Free · No ads · No sign-up · Made for kids'),
      this.installRow(),
    );
    this.ui.append(gate);
  }

  /** "Install Zapboard" on the start screen: a button where the browser allows it, a tip on iPhone. */
  private installRow(): HTMLElement {
    const row = h('div.install');
    const render = () => {
      row.replaceChildren();
      if (isInstalled()) return;
      if (canPrompt()) {
        row.append(
          h(
            'button.btn.white.install-btn',
            {
              onclick: async () => {
                if (await promptInstall()) this.brain.say('Zapboard is on your home screen now! 🎉', 'wow');
              },
            },
            '📲 Install Zapboard',
          ),
        );
      } else if (isIosSafari()) {
        row.append(
          h(
            'div.install-tip',
            {},
            'Play like an app: tap ',
            h('b', {}, 'Share ⬆️'),
            ', then ',
            h('b', {}, 'Add to Home Screen'),
            '.',
          ),
        );
      }
    };
    render();
    onInstallChange(render);
    return row;
  }

  showMap() {
    this.voice.stop();
    const progress = store.get<Record<string, number>>('progress') ?? {};
    let nextFound = false;
    const levelCard = (l: Level, i: number) => {
      const st = progress[l.id] ?? 0;
      const unlocked = i === 0 || (progress[ALL_LEVELS[i - 1]!.id] ?? 0) > 0 || st > 0;
      const isNext = unlocked && !st && !nextFound;
      if (isNext) nextFound = true;
      return h(
        `button.level-card${unlocked ? '' : '.locked'}${isNext ? '.next' : ''}` as 'button',
        {
          onclick: () =>
            unlocked
              ? this.startLevel(l)
              : this.brain.say('Finish the level before to unlock this one!', 'think'),
        },
        h('span.num', {}, String(l.num)),
        h('span.emoji', {}, unlocked ? l.emoji : '🔒'),
        h('span.t', {}, l.title),
        h('span.stars', {}, st ? '⭐'.repeat(st) + '☆'.repeat(3 - st) : '☆☆☆'),
      );
    };
    const worlds = WORLDS.map((w) => {
      const levels = ALL_LEVELS.map((l, i) => [l, i] as const).filter(([l]) => l.world === w.num);
      return h(
        `section.world.world-${w.num}` as 'section',
        {},
        h('div.world-title', {}, `World ${w.num}: ${w.title} ${w.emoji}`),
        levels.length
          ? h(
              'div.levels',
              {},
              levels.map(([l, i]) => levelCard(l, i)),
            )
          : h('div.world-soon', {}, 'Coming soon 🔒'),
      );
    });
    const card = h(
      'div.card.map',
      {},
      h('h2', {}, '🗺️ Levels'),
      h('div.worlds', {}, worlds),
      h('div.row', {}, h('button.btn.white', { onclick: () => this.startFree() }, 'Free Build 🛠️')),
    );
    this.overlay(card);
  }

  private overlay(card: HTMLElement, dismissable = true) {
    this.closeOverlays();
    const o = h('div.overlay', {
      onclick: (e: Event) => {
        if (dismissable && e.target === o) o.remove();
      },
    });
    o.append(card);
    this.ui.append(o);
    return o;
  }

  private closeOverlays() {
    this.ui.querySelectorAll('.overlay').forEach((o) => o.remove());
  }

  // ───────────────────────────── Top bar ─────────────────────────────
  private topbar(): HTMLElement {
    const toggle = (label: string, icon: [string, string], initial: boolean, fn: (on: boolean) => void) => {
      const b = h(
        'button.icon-btn',
        { 'aria-label': label, title: label, 'aria-pressed': String(initial) },
        initial ? icon[0] : icon[1],
      );
      let on = initial;
      b.addEventListener('click', () => {
        on = !on;
        b.textContent = on ? icon[0] : icon[1];
        b.setAttribute('aria-pressed', String(on));
        fn(on);
      });
      return b;
    };
    this.undoBtn = h(
      'button.icon-btn',
      { 'aria-label': 'Undo', title: 'Undo', onclick: () => this.undo() },
      '↶',
    );
    this.redoBtn = h(
      'button.icon-btn.opt',
      { 'aria-label': 'Redo', title: 'Redo', onclick: () => this.redo() },
      '↷',
    );
    this.stage.flow.enabled = store.get<boolean>('flow') ?? true;
    const soundOn = store.get<boolean>('sound') ?? true;
    const voiceOn = store.get<boolean>('voice') ?? true;
    this.audio.setMuted(!soundOn);
    this.voice.enabled = voiceOn;
    return h(
      'div.topbar',
      {},
      h(
        'button.logo',
        { onclick: () => this.showMap(), 'aria-label': 'Zapboard: open levels' },
        h('span.bolt', {}, '⚡'),
        h('b', {}, 'Zapboard'),
      ),
      this.titleEl,
      h('div.spacer'),
      toggle('Electricity flow', ['⚡', '⚡'], this.stage.flow.enabled, (on) => {
        this.stage.flow.enabled = on;
        store.set('flow', on);
        if (on)
          this.brain.say(
            'Electricity vision on! Watch it flow out of the battery, around the loop, and back home. ⚡',
            'wow',
          );
      }),
      toggle('Lights out', ['🌙', '🌙'], false, (on) => {
        this.stage.night = on;
        document.body.classList.toggle('night', on);
        if (on) this.brain.say('Lights out! Now you can really see your lights glow. 🌙', 'wow');
      }),
      h(
        'button.icon-btn.opt',
        {
          'aria-label': 'Show whole board',
          title: 'Show whole board',
          onclick: () => this.stage.resetView(),
        },
        '⤢',
      ),
      this.undoBtn,
      this.redoBtn,
      h(
        'button.icon-btn',
        { 'aria-label': 'Start over', title: 'Start over', onclick: () => this.clearBoard() },
        '🧹',
      ),
      toggle('Sound', ['🔊', '🔇'], soundOn, (on) => {
        this.audio.setMuted(!on);
        store.set('sound', on);
      }),
      toggle('Read aloud', ['🗣️', '🤐'], voiceOn, (on) => {
        this.voice.enabled = on;
        if (!on) this.voice.stop();
        store.set('voice', on);
      }),
    );
  }

  // ───────────────────────────── Part menu ─────────────────────────────
  private buildMenu(id: string) {
    const p = this.board.parts.get(id)!;
    const def = partDef(p.type);
    const locked = this.levelMode?.locked.has(id);
    const btn = (icon: string, label: string, fn: () => void, cls = '') =>
      h(
        `button.icon-btn${cls}` as 'button',
        { 'aria-label': label, title: label, onclick: (e: Event) => (e.stopPropagation(), fn()) },
        icon,
      );
    const items: HTMLElement[] = [h('span.name', {}, def.name)];
    if (!locked) {
      if (p.type !== 'jumper') items.push(btn('⟳', 'Turn', () => this.edit(id, 'rotate')));
      if (['battery', 'led', 'motor'].includes(p.type))
        items.push(btn('⇄', 'Flip around', () => this.edit(id, 'flip')));
      if (p.type === 'wire' || p.type === 'jumper' || p.type === 'led')
        items.push(btn('🎨', 'Change color', () => this.edit(id, 'color')));
      if (p.type === 'resistor') items.push(btn('Ω', 'Change size', () => this.edit(id, 'ohms')));
    }
    items.push(btn('ℹ️', 'Learn about it', () => this.info(id)));
    if (!locked) items.push(btn('🗑️', 'Remove', () => this.edit(id, 'remove'), '.danger'));
    this.menu.replaceChildren(...items);
  }

  private edit(id: string, what: 'rotate' | 'flip' | 'color' | 'shorter' | 'longer' | 'ohms' | 'remove') {
    const p = this.board.parts.get(id);
    if (!p) return;
    let cmd: Command;
    switch (what) {
      case 'rotate':
        cmd = { op: 'move', id, ...rotated(p) };
        break;
      case 'flip':
        cmd = { op: 'move', id, ...flipped(p) };
        break;
      case 'color': {
        const names = p.type === 'led' ? Object.keys(LED_COLORS) : WIRE_COLOR_NAMES;
        const i = names.indexOf(String(p.props.color));
        cmd = { op: 'set', id, key: 'color', value: names[(i + 1) % names.length]! };
        break;
      }
      case 'shorter':
      case 'longer': {
        const n = Number(p.props.length) + (what === 'longer' ? 1 : -1);
        if (n < 2 || n > 6) return;
        cmd = { op: 'set', id, key: 'length', value: n };
        break;
      }
      case 'ohms': {
        const vals = [100, 1000, 10000];
        cmd = {
          op: 'set',
          id,
          key: 'ohms',
          value: vals[(vals.indexOf(Number(p.props.ohms)) + 1) % vals.length]!,
        };
        break;
      }
      case 'remove':
        cmd = { op: 'remove', id };
        break;
    }
    if (this.run(cmd)) {
      if (what === 'remove') this.audio.poof();
      else {
        this.audio.snap();
        Haptics.snap();
        this.stage.bump(id);
      }
    }
  }

  private positionMenu() {
    if (!this.menuFor) return;
    const pos = this.stage.screenOf(this.menuFor, 0.9);
    if (!pos) return;
    const w = this.menu.offsetWidth;
    const x = Math.min(innerWidth - w / 2 - 8, Math.max(w / 2 + 8, pos.x));
    const y = Math.max(this.menu.offsetHeight + 70, pos.y - 10);
    this.menu.style.left = `${x}px`;
    this.menu.style.top = `${y}px`;
  }

  // ───────────────────────────── Fact cards ─────────────────────────────
  /** Tapping a tray item drops it onto the board in a smart spot (little kids tap more than drag). */
  private trayTap(entry: TrayEntry) {
    const def = partDef(entry.type);
    if (entry.left === 0) return;
    const spot = findSpot(this.board, entry.type, entry.props);
    if (!spot) {
      this.tray.wiggle(entry.key);
      this.brain.say('The board is full! Drag a part onto the tray to make room.', 'think');
      return;
    }
    const id = this.board.newId();
    if (this.run({ op: 'place', part: { ...spot, id, layer: 0 } }, { snapped: id })) {
      if (!this.tappedOnce) {
        this.tappedOnce = true;
        this.brain.say(
          `Here's the ${def.name.toLowerCase()}! Drag it to move it, or tap it to learn more.`,
          'happy',
        );
      }
    }
  }
  private tappedOnce = false;

  factCard(type: PartType, props: PartProps) {
    const def = partDef(type);
    const img = this.thumbs.get(thumbKey(type, props)) ?? this.thumbs.get(thumbKey(type));
    const tabs: [string, string][] = [
      ['What it does', def.facts.what],
      ['Find it at home', def.facts.home],
      ['Real world', def.facts.real],
    ];
    const body = h('div.fact-body');
    const tabEls = tabs.map(([label], i) =>
      h('button.tab', { role: 'tab', 'aria-selected': String(i === 0), onclick: () => show(i) }, label),
    );
    const show = (i: number) => {
      tabEls.forEach((t, j) => t.setAttribute('aria-selected', String(i === j)));
      const [, text] = tabs[i]!;
      body.replaceChildren(h('p', {}, text));
      if (type === 'battery' && i === 2) body.append(batteryGuide());
      this.voice.say(text);
    };
    const card = h(
      'div.card',
      {},
      h('div.fact-head', {}, img ? h('img', { src: img, alt: '' }) : null, h('h2', {}, def.name)),
      h('div.tabs', { role: 'tablist' }, tabEls),
      body,
      type === 'battery'
        ? h(
            'div.safety',
            {},
            '⚠️ Never play with wall sockets! They are much, much stronger than batteries. If a battery gets hot, tell a grown-up.',
          )
        : null,
      h(
        'div.row',
        {},
        h(
          'button.btn.white',
          { onclick: () => show(tabEls.findIndex((t) => t.getAttribute('aria-selected') === 'true')) },
          '🔊 Read it',
        ),
        h('button.btn', { onclick: () => (this.voice.stop(), this.closeOverlays()) }, 'Got it! 👍'),
      ),
    );
    this.overlay(card);
    show(0);
  }

  // ───────────────────────────── Layout & keys ─────────────────────────────
  relayout() {
    // Phones (portrait), phones held sideways (short), and tablets in portrait all get the
    // touch layout with the coach card. Only roomy screens get the desktop layout.
    const short = innerHeight < 540 && innerWidth > innerHeight;
    const narrow = innerWidth < 760 || innerHeight > innerWidth * 1.1;
    this.setPhone(narrow || short);
    if (narrow || short) {
      this.relayoutPhone(short && !narrow);
      return;
    }
    this.ui.classList.remove('coach-top', 'short');
    this.layout = narrow ? 'bottom' : 'left';
    this.tray.setLayout(this.layout);
    this.ui.classList.toggle('layout-bottom', narrow);
    this.ui.classList.toggle('layout-left', !narrow);
    document.body.classList.toggle('layout-bottom', narrow);
    // The checklist sits inside the top bar on wide screens (saves height), below it otherwise.
    // Inline only if it actually fits between the title and the buttons.
    let inlineBar = !narrow && innerWidth >= 1260;
    this.levelbar.classList.toggle('inline', inlineBar);
    if (inlineBar && this.levelbar.style.display !== 'none') {
      const r = this.levelbar.getBoundingClientRect();
      const buttons = this.ui.querySelector('.topbar .spacer')?.nextElementSibling?.getBoundingClientRect();
      const title = this.titleEl.getBoundingClientRect();
      if ((buttons && r.right > buttons.left - 8) || r.left < title.right + 8) {
        inlineBar = false;
        this.levelbar.classList.remove('inline');
      }
    }
    const lb = this.levelbar.style.display !== 'none' ? this.levelbar.getBoundingClientRect() : null;
    const trayRect = this.tray.el.getBoundingClientRect();
    const top = Math.max(62, lb && !inlineBar ? lb.bottom + 6 : 62);
    const left = narrow ? 8 : trayRect.right + 6;
    const trayBottom = narrow ? innerHeight - trayRect.top : 0;

    // Put Sparky where the spare room is: beside the board if the screen is wider than the board
    // needs, below it otherwise. Pick whichever leaves the board bigger.
    const b = this.board;
    const elev = Math.sin((57 * Math.PI) / 180);
    const boardSize = (portrait: boolean) => {
      const w = (portrait ? b.rows : b.cols) + 1;
      const h = ((portrait ? b.cols : b.rows) + 1) * elev + 0.8;
      return { w, h };
    };
    const scaleFor = (sw: number, sh: number) => {
      const bs = boardSize(sw / sh < 0.85);
      return Math.min(sw / bs.w, sh / bs.h);
    };
    const COL = innerWidth < 560 ? 200 : 260;
    const STRIP = innerWidth < 560 ? 100 : 104;
    const rightScale = scaleFor(innerWidth - left - COL, innerHeight - top - trayBottom - 8);
    const bottomScale = scaleFor(innerWidth - left - 8, innerHeight - top - trayBottom - STRIP);
    const coachRight = rightScale >= bottomScale && innerWidth >= 700;
    this.ui.classList.toggle('coach-right', coachRight);
    this.ui.classList.toggle('coach-bottom', !coachRight);
    this.stage.insets = {
      top,
      right: coachRight ? COL : 8,
      bottom: trayBottom + (coachRight ? 8 : STRIP),
      left,
    };
    this.stage.resize();
  }

  /**
   * Phones and portrait tablets (mobile-first): top bar, ONE coach strip (Sparky + the current
   * goal, or what he's saying), the board standing tall in the middle, only parts at the bottom.
   */
  /** Switch Sparky between his floating bubble (desktop) and the coach card (phones). */
  private setPhone(on: boolean) {
    if (on === this.phone) return;
    this.phone = on;
    this.sparky.onSay = on ? (text, _mood, hold) => this.cardSay(text, hold) : null;
    this.sparky.onQuiet = on ? () => this.avatarEl.classList.remove('talking') : null;
    if (!on) {
      this.levelbar.classList.remove('speaking');
      window.clearTimeout(this.speechTimer);
    } else this.sparky.hide();
  }

  /**
   * Touch layout. Portrait: top bar, coach card, board, parts tray along the bottom.
   * Short (phone sideways): parts tray down the left, coach card across the top, board below.
   * Everything is measured from the real elements, so nothing can overlap the board.
   */
  private relayoutPhone(short: boolean) {
    this.layout = short ? 'left' : 'bottom';
    this.tray.setLayout(this.layout);
    for (const c of ['coach-right', 'coach-bottom']) this.ui.classList.remove(c);
    this.ui.classList.add('coach-top');
    this.ui.classList.toggle('short', short);
    this.ui.classList.toggle('layout-bottom', !short);
    this.ui.classList.toggle('layout-left', short);
    document.body.classList.toggle('layout-bottom', !short);
    this.levelbar.classList.remove('inline');
    const bar = this.ui.querySelector('.topbar')!.getBoundingClientRect();
    const trayRect = this.tray.el.getBoundingClientRect();
    this.ui.style.setProperty('--strip-top', `${bar.bottom + 6}px`);
    this.ui.style.setProperty('--strip-left', short ? `${trayRect.right + 8}px` : '8px');
    const card = this.levelbar.style.display !== 'none' ? this.levelbar.getBoundingClientRect() : null;
    const top = Math.max(bar.bottom, card ? card.bottom : 0) + 6;
    this.stage.insets = short
      ? { top, right: 6, bottom: 6, left: trayRect.right + 6 }
      : { top, right: 6, bottom: innerHeight - trayRect.top + 6, left: 6 };
    this.stage.resize();
  }

  private onKey(e: KeyboardEvent) {
    if (e.target instanceof HTMLInputElement) return;
    const mod = e.ctrlKey || e.metaKey;
    if (mod && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      if (e.shiftKey) this.redo();
      else this.undo();
    } else if (mod && e.key.toLowerCase() === 'y') {
      e.preventDefault();
      this.redo();
    } else if (this.menuFor && !this.controller.dragging) {
      if (e.key === 'r' || e.key === 'R') this.edit(this.menuFor, 'rotate');
      else if (e.key === 'f' || e.key === 'F') this.edit(this.menuFor, 'flip');
      else if (e.key === 'Delete' || e.key === 'Backspace') this.edit(this.menuFor, 'remove');
      else if (e.key === 'i' || e.key === 'I') this.info(this.menuFor);
      else if (e.key === 'Escape') this.select(null);
    }
  }
}

/**
 * A copy of Sparky's SVG with its own gradient ids. Copies that share ids with a hidden
 * original lose their fills in some browsers (that made the card's Sparky look washed out).
 */
function sparkySvg(sparky: Sparky, suffix: string): string {
  return sparky.el
    .querySelector('svg')!
    .outerHTML.replace(/id="spk-(\w+)"/g, `id="spk-$1-${suffix}"`)
    .replace(/url\(#spk-(\w+)\)/g, `url(#spk-$1-${suffix})`);
}

function trayLabel(t: TrayItem): string {
  if (t.type === 'wire') return 'Wire';
  if (t.type === 'led' && t.props?.color) {
    const c = String(t.props.color);
    return `${c[0]!.toUpperCase()}${c.slice(1)} LED`;
  }
  return (
    {
      battery: 'Battery',
      lamp: 'Bulb',
      led: 'LED',
      motor: 'Motor',
      switch: 'Switch',
      button: 'Button',
      buzzer: 'Buzzer',
      resistor: 'Resistor',
      jumper: 'Cable',
    }[t.type] ?? t.type
  );
}

function batteryGuide(): HTMLElement {
  const cells: [string, string, string, number, number][] = [
    ['AAA', '1.5 V', 'remotes, small toys', 46, 12],
    ['AA', '1.5 V', 'toys, clocks, this kit!', 54, 16],
    ['C', '1.5 V', 'big flashlights', 54, 26],
    ['D', '1.5 V', 'radios, big toys', 60, 32],
    ['9V', '9 V', 'smoke alarms', 44, 26],
    ['Coin CR2032', '3 V', 'watches, car keys', 26, 26],
  ];
  return h(
    'div.battery-guide',
    {},
    cells.map(([name, v, use, w, hgt]) =>
      h(
        'div.cell-card',
        {},
        h('div.bat', {
          style: {
            width: `${w}px`,
            height: `${hgt}px`,
            borderRadius: name.startsWith('Coin') ? '50%' : '6px',
          },
        }),
        name,
        h('small', {}, v),
        h('small', {}, use),
      ),
    ),
  );
}

export { sameItem };
